// src/Pages/Toolbox.jsx
import React from "react";
import { motion, useInView } from "framer-motion";
import { Blob3 } from "../Components/blobs/Blob3";

import img2 from "./img/skillimg2.png";

// ── Your original local images ────────────────────────────────────────────────
import py   from "./img/Python.png";
import js   from "./img/js.png";
import html from "./img/html.png";
import css  from "./img/css.png";
import re   from "./img/React.png";
import md   from "./img/Mongodb.png";
import fig  from "./img/Figma.png";
import fm   from "./img/framer.png";

// ── Slide animations (your original, unchanged) ───────────────────────────────
const slideLeft = {
  initial: { opacity: 0, x: -60 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};
const slideRight = {
  initial: { opacity: 0, x: 60 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

// ── Tool columns ──────────────────────────────────────────────────────────────
const toolColumns = [
  {
    heading: "Languages",
    items: [
      { label: "Python",        img: py,    local: true },
      { label: "JavaScript",    img: js,    local: true },
      { label: "HTML",          img: html,  local: true },
      { label: "CSS",           img: css,   local: true },
      { label: "TypeScript",    img: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg" },
      { label: "Dart",          img: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/dart/dart-original.svg" },
      { label: "Solidity",      img: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/solidity/solidity-original.svg" },
      { label: "Rust",          img: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/rust/rust-original.svg" },
      { label: "SQL",           img: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg" },
    ],
  },
  {
    heading: "Frameworks",
    items: [
      { label: "React",         img: re,    local: true },
      { label: "Framer Motion", img: fm,    local: true, invert: true },
      { label: "Next.js",       img: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg", invert: true },
      { label: "Flutter",       img: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flutter/flutter-original.svg" },
      { label: "Node.js",       img: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg" },
      { label: "Express",       img: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg", invert: true },
      { label: "Tailwind CSS",  img: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tailwindcss/tailwindcss-original.svg" },
      { label: "GSAP",          img: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/gsap/gsap-original.svg" },
      { label: "ethers.js",     img: "https://avatars.githubusercontent.com/u/56928858?s=200&v=4" },
    ],
  },
  {
    heading: "Tools & Platforms",
    items: [
      { label: "Base",          img: "https://avatars.githubusercontent.com/u/108554348?s=200&v=4" },
      { label: "Hardhat",       img: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/hardhat/hardhat-original.svg" },
      { label: "n8n",           img: "https://avatars.githubusercontent.com/u/45487711?s=200&v=4" },
      { label: "Figma",         img: fig,   local: true },
      { label: "MongoDB",       img: md,    local: true },
      { label: "Firebase",      img: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg" },
      { label: "Supabase",      img: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/supabase/supabase-original.svg" },
      { label: "PostgreSQL",    img: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg" },
      { label: "Docker",        img: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg" },
      { label: "Git",           img: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg" },
    ],
  },
];

// ── Premium item animation variants ──────────────────────────────────────────
// Entrance: clip-path wipe from bottom + y rise + fade
// Exit: reverse wipe up + fade (optional out)
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 18,
    clipPath: "inset(100% 0% 0% 0%)",   // fully clipped from bottom
  },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    clipPath: "inset(0% 0% 0% 0%)",      // fully revealed
    transition: {
      duration: 0.55,
      delay: i * 0.055,
      ease: [0.22, 1, 0.36, 1],          // expo out — snappy premium feel
    },
  }),
  exit: (i) => ({
    opacity: 0,
    y: -10,
    clipPath: "inset(0% 0% 100% 0%)",    // wipes out upward
    transition: {
      duration: 0.3,
      delay: i * 0.025,                  // faster stagger on exit
      ease: [0.4, 0, 1, 1],
    },
  }),
};

// Column heading variant — line draws in from left
const headingVariants = {
  hidden:  { opacity: 0, x: -16 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
  exit: (i) => ({
    opacity: 0,
    x: 10,
    transition: { duration: 0.25, delay: i * 0.04 },
  }),
};

// Divider line draws across
const lineVariants = {
  hidden:  { scaleX: 0, originX: 0 },
  visible: (i) => ({
    scaleX: 1,
    transition: { duration: 0.55, delay: i * 0.12 + 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
  exit: (i) => ({
    scaleX: 0,
    originX: 1,                          // retracts from right on exit
    transition: { duration: 0.25, delay: i * 0.04 },
  }),
};

// ── Single tool item ──────────────────────────────────────────────────────────
function ToolItem({ label, img, invert = false, itemIndex, colIndex, isInView }) {
  const [hovered, setHovered] = React.useState(false);

  // flat stagger index across all columns
  const i = colIndex * 12 + itemIndex;

  return (
    <motion.li
      custom={i}
      variants={itemVariants}
      style={{ listStyle: "none", display: "flex", alignItems: "center", gap: 0 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* icon — slides in from left on hover */}
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          width: hovered ? "22px" : "0px",
          opacity: hovered ? 1 : 0,
          overflow: "hidden",
          transition: "width 0.2s cubic-bezier(0.22,1,0.36,1), opacity 0.15s ease",
          flexShrink: 0,
        }}
      >
        <img
          src={img}
          alt={label}
          width={16}
          height={16}
          style={{
            objectFit: "contain",
            flexShrink: 0,
            filter: invert ? "invert(1) brightness(0.3)" : "none",
          }}
          onError={(e) => { e.currentTarget.style.opacity = "0"; }}
        />
      </span>

      {/* label */}
      <span
        className="font-pop font-semibold"
        style={{
          fontSize: "clamp(13px, 1.4vw, 15px)",
          transition: "all 0.25s cubic-bezier(0.22,1,0.36,1)",
          cursor: "default",
          userSelect: "none",
          ...(hovered
            ? {
                background: "linear-gradient(to right, #f472b6, #a855f7)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                transform: "translateX(3px)",
              }
            : {
                color: "#a3a3a3",
                transform: "translateX(0px)",
              }
          ),
        }}
      >
        {label}
      </span>
    </motion.li>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function Toolbox() {
  const sectionRef = React.useRef(null);

  // useInView with margin so animation triggers slightly before fully visible
  const isInView = useInView(sectionRef, {
    once: false,          // re-animates every time section enters
    amount: 0.15,
    margin: "0px 0px -80px 0px",
  });

  return (
    <section
      id="Toolbox"
      data-theme="light"
      className="full-bleed bg-[#fdfff8] text-black relative"
      style={{
        "--padY": "clamp(16rem, 6vw, 8rem)",
        "--colGap": "clamp(2.5rem, 5vw, 8rem)",
        "--imgMax": "340px",
        "--imgVW": "40vw",
        "--maxW": "1200px",
        "--textPadL": "clamp(0rem, 4vw, 2.5rem)",
        "--imgOffsetX": "0px",
        "--imgOffsetY": "0px",
        "--blobOffsetX": "0px",
        "--blobOffsetY": "0px",
        "--blobScale": "1",
      }}
    >
      <div className="absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-black/10 to-transparent pointer-events-none" />

      <div ref={sectionRef} className="container min-h-[calc(100svh-var(--nav-h))] py-[var(--padY)]">
        <div
          className="
            grid items-center
            gap-12 md:gap-[var(--colGap)]
            md:grid-cols-2
            justify-items-center md:justify-items-start
            text-center md:text-left
            mx-auto
          "
          style={{ maxWidth: "var(--maxW)" }}
        >
          {/* Left — image + blob (your original, unchanged) */}
          <motion.div
            className="relative flex justify-center order-last md:order-first"
            variants={slideLeft}
            initial="initial"
            whileInView="animate"
            viewport={{ once: false, amount: 0.3 }}
          >
            <div
              className="
                relative flex justify-center
                [--imgOffsetX:0px] [--imgOffsetY:0px]
                [--blobOffsetX:0px] [--blobOffsetY:0px] [--blobScale:1]
                md:[--imgOffsetX:-0.5rem]
                md:[--blobOffsetX:-0.25rem] md:[--blobOffsetY:0.5rem] md:[--blobScale:1.06]
                lg:[--imgOffsetX:2rem] lg:[--blobOffsetY:0.75rem]
              "
              style={{
                transform: `translate(var(--imgOffsetX), var(--imgOffsetY))`,
                willChange: "transform",
              }}
            >
              <img
                src={img2}
                alt="Toolbox visual"
                className="
                  z-10
                  w-[min(50vw,var(--imgMax))]
                  md:w-[min(var(--imgVW),var(--imgMax))]
                  h-auto object-contain animate-levitate4
                "
              />
              <div
                className="absolute -z-10 inset-0 flex items-center justify-center pointer-events-none"
                style={{
                  transform: `translate(var(--blobOffsetX), var(--blobOffsetY)) scale(var(--blobScale))`,
                }}
              >
                <Blob3 />
              </div>
            </div>
          </motion.div>

          {/* Right — heading + skill columns */}
          <div className="space-y-6 max-w-[60ch] lg:pl-[var(--textPadL)] w-full">
            <motion.h3
              className="font-sat font-bold text-3xl md:text-4xl lg:text-5xl"
              variants={slideRight}
              initial="initial"
              whileInView="animate"
              viewport={{ once: false, amount: 0.3 }}
            >
              my digital tool box.
            </motion.h3>

            <motion.p
              className="font-sat text-base md:text-lg lg:text-xl text-neutral-700"
              variants={slideRight}
              initial="initial"
              whileInView="animate"
              viewport={{ once: false, amount: 0.3 }}
            >
              A curated set of tools to streamline full-stack development—from
              front-end design to back-end infrastructure and deployment.
            </motion.p>

            {/* ── 3-column skill list with premium stagger ── */}
            <div className="grid grid-cols-3 gap-x-6 pt-2 text-left">
              {toolColumns.map((col, colIndex) => (
                <div key={col.heading}>

                  {/* column heading — draws in */}
                  <motion.p
                    custom={colIndex}
                    variants={headingVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "exit"}
                    className="font-mono mb-2"
                    style={{
                      fontSize: "12px",
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      background: "linear-gradient(to right, #ED738F, #AB45CA)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {col.heading}
                  </motion.p>

                  {/* divider — scales in from left */}
                  <motion.div
                    custom={colIndex}
                    variants={lineVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "exit"}
                    style={{
                      height: "1px",
                      background: "rgba(0,0,0,0.1)",
                      marginBottom: "12px",
                      transformOrigin: "left",
                    }}
                  />

                  {/* items — staggered clip-path wipe */}
                  <motion.ul
                    style={{ padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "9px" }}
                    initial="hidden"
                    animate={isInView ? "visible" : "exit"}
                  >
                    {col.items.map((item, itemIndex) => (
                      <ToolItem
                        key={item.label}
                        label={item.label}
                        img={item.img}
                        invert={item.invert || false}
                        itemIndex={itemIndex}
                        colIndex={colIndex}
                        isInView={isInView}
                      />
                    ))}
                  </motion.ul>
                </div>
              ))}
            </div>

          </div>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-black/10 to-transparent pointer-events-none" />
    </section>
  );
}